import React, { useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import './TlcPage.css';

const TlcPage = ({ isAdmin = false }) => {
    const navigate = useNavigate();
    const { submissionId, coeName } = useParams();

    const getBaseReviewPath = () => {
        if (coeName) return `/${coeName}/learner-reviews`;
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (user?.role === 'coe_report_viewer') {
            const formattedCoeName = user?.coeName ? user.coeName.trim().replace(/\s+/g, '-') : (user?.coe_name ? user.coe_name.trim().replace(/\s+/g, '-') : 'coe');
            return `/${formattedCoeName}/learner-reviews`;
        }
        return isAdmin ? '/admin/learner-reviews' : '/mentor/learner-reviews';
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const CircularScore = ({ score }) => {
        return (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={100} sx={{ color: '#e0e0e0' }} size={60} thickness={4} />
                <CircularProgress variant="determinate" value={score} sx={{ color: '#1976d2', position: 'absolute', left: 0 }} size={60} thickness={4} />
                <Box sx={{
                    top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <Typography variant="caption" component="div" color="text.primary" sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                        {`${Math.round(score)}%`}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box className="tlc-page-container">
            <Box className="tlc-breadcrumb">
                <span className="breadcrumb-link" onClick={() => navigate(getBaseReviewPath())}>Learner Review</span> &gt;{' '}
                <span className="breadcrumb-link" onClick={() => navigate(`${getBaseReviewPath()}/${submissionId}`)}>Review</span> &gt; TLC
            </Box>

            <Box className="tlc-header-container">
                <Box>
                    <Typography className="tlc-title">TLC Parameters - Details</Typography>
                    <Box className="tlc-learner-info">
                        <span className="tlc-learner-name">Shubham Chaudhari</span>
                        <span className="tlc-status-pill">PENDING REVIEW</span>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                    <Box className="tlc-pagination">
                        <span className="tlc-pagination-btn">&lt; Prev</span>
                        <span>REVIEW 4 OF 12</span>
                        <span className="tlc-pagination-btn">Next &gt;</span>
                    </Box>
                    <Box className="tlc-overall-score-card">
                        <CircularScore score={82} />
                        <Box className="tlc-overall-score-text-block">
                            <span className="tlc-overall-label">OVERALL TLC SCORE</span>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span className="tlc-overall-value">82.4</span>
                                <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: 600 }}>/ 100</span>
                            </Box>
                            <span className="tlc-overall-max">Max Score: 100 indicator</span>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box className="tlc-section-header">
                <Typography className="tlc-section-title">TECHABILITY</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    <span className="tlc-section-score">80/100</span>
                    <span style={{ fontSize: '13px', color: '#6c757d' }}>Score (%)</span>
                </Box>
            </Box>

            <Box className="tech-excellence-cards">
                <Box className="tlc-tech-card">
                    <span className="tlc-tech-card-title">CONCEPTS (OBJ)</span>
                    <span className="tlc-tech-card-value">100%</span>
                </Box>
                <Box className="tlc-tech-card">
                    <span className="tlc-tech-card-title">CONCEPTS (SUBJ)</span>
                    <span className="tlc-tech-card-value">20%</span>
                </Box>
                <Box className="tlc-tech-card">
                    <span className="tlc-tech-card-title">COMPLETENESS</span>
                    <span className="tlc-tech-card-value">10%</span>
                </Box>
                <Box className="tlc-tech-card">
                    <span className="tlc-tech-card-title">LOGICAL THINKING</span>
                    <span className="tlc-tech-card-value">40%</span>
                </Box>
                <Box className="tlc-tech-card">
                    <span className="tlc-tech-card-title">CODE QUALITY</span>
                    <span className="tlc-tech-card-value">5%</span>
                </Box>
            </Box>

            <Box className="tlc-columns-container">
                {/* Learnability Column */}
                <Box className="tlc-column">
                    <Box>
                        <Typography className="tlc-section-title">LEARNABILITY</Typography>
                        <Typography className="tlc-section-subtitle">Attendance and Diligence</Typography>
                    </Box>

                    {/* Regularity Card */}
                    <Box className="tlc-parameter-card">
                        <Box className="tlc-parameter-header">
                            <Box className="tlc-parameter-icon">
                                <CalendarTodayIcon fontSize="small" />
                            </Box>
                            <Box className="tlc-parameter-info">
                                <Typography className="tlc-parameter-title">Regularity (Attendance in session)</Typography>
                                <Typography className="tlc-parameter-desc">Presence and active participation</Typography>
                            </Box>
                            <Box className="tlc-parameter-score-box">
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                    <span className="tlc-parameter-score-top">50</span>
                                    <span style={{ fontSize: '13px', color: '#1976d2' }}>/ 50%</span>
                                </Box>
                                <span className="tlc-parameter-score-bottom">Weighted average</span>
                            </Box>
                        </Box>
                        <Box className="tlc-progress-bar-container">
                            <Box className="tlc-progress-fill" style={{ width: '100%' }}></Box>
                        </Box>
                        <Box className="tlc-progress-labels">
                            <span>0</span>
                            <span>100%</span>
                        </Box>
                    </Box>

                    {/* Finish Work Diligently Card */}
                    <Box className="tlc-parameter-card">
                        <Box className="tlc-parameter-header">
                            <Box className="tlc-parameter-icon">
                                <CheckCircleOutlineIcon fontSize="small" />
                            </Box>
                            <Box className="tlc-parameter-info">
                                <Typography className="tlc-parameter-title">Finish Work Diligently</Typography>
                                <Typography className="tlc-parameter-desc">Timely submission and quality</Typography>
                            </Box>
                            <Box className="tlc-parameter-score-box">
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                    <span className="tlc-parameter-score-top">25</span>
                                    <span style={{ fontSize: '13px', color: '#1976d2' }}>/ 50%</span>
                                </Box>
                                <span className="tlc-parameter-score-bottom">Weighted average</span>
                            </Box>
                        </Box>
                        <Box className="tlc-progress-bar-container">
                            <Box className="tlc-progress-fill" style={{ width: '50%' }}></Box>
                        </Box>
                        <Box className="tlc-progress-labels">
                            <span>0</span>
                            <span>100%</span>
                        </Box>
                    </Box>

                    <Box className="tlc-manual-guidance">
                        <Box className="tlc-guidance-title">
                            <InfoOutlinedIcon fontSize="small" />
                            <span>Manual Entry Guidance</span>
                        </Box>
                        <ul className="tlc-guidance-list">
                            <li>80% and above, score 100% of max</li>
                            <li>60 to less than 80%, score 75% of max</li>
                            <li>50 to less than 60%, score 50% of Max</li>
                            <li>Less than 50%, Score 30% of max</li>
                            <li>No attendance, 0%</li>
                        </ul>
                    </Box>
                </Box>

                {/* Communicability Column */}
                <Box className="tlc-column">
                    <Box>
                        <Typography className="tlc-section-title">COMMUNICABILITY</Typography>
                        <Typography className="tlc-section-subtitle">Interaction and Clarity</Typography>
                    </Box>

                    {/* Ability to Comprehend */}
                    <Box className="tlc-parameter-card">
                        <Box className="tlc-parameter-header">
                            <Box className="tlc-parameter-icon">
                                <EmojiObjectsOutlinedIcon fontSize="small" />
                            </Box>
                            <Box className="tlc-parameter-info">
                                <Typography className="tlc-parameter-title">Ability to Comprehend</Typography>
                                <Typography className="tlc-parameter-desc">Understanding complex prompts</Typography>
                            </Box>
                            <Box className="tlc-parameter-score-box">
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                    <span className="tlc-parameter-score-top">8</span>
                                    <span style={{ fontSize: '13px', color: '#1976d2' }}>/ 40%</span>
                                </Box>
                                <span className="tlc-parameter-score-bottom">Weighted average</span>
                            </Box>
                        </Box>
                        <Box className="tlc-progress-bar-container">
                            <Box className="tlc-progress-fill" style={{ width: '20%' }}></Box>
                        </Box>
                        <Box className="tlc-progress-labels">
                            <span>0</span>
                            <span>100%</span>
                        </Box>
                    </Box>

                    {/* Verbal Communication */}
                    <Box className="tlc-parameter-card">
                        <Box className="tlc-parameter-header">
                            <Box className="tlc-parameter-icon">
                                <RecordVoiceOverOutlinedIcon fontSize="small" />
                            </Box>
                            <Box className="tlc-parameter-info">
                                <Typography className="tlc-parameter-title">Verbal Communication</Typography>
                                <Typography className="tlc-parameter-desc">Clarity and pronunciation</Typography>
                            </Box>
                            <Box className="tlc-parameter-score-box">
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                    <span className="tlc-parameter-score-top">6</span>
                                    <span style={{ fontSize: '13px', color: '#1976d2' }}>/ 30%</span>
                                </Box>
                                <span className="tlc-parameter-score-bottom">Weighted average</span>
                            </Box>
                        </Box>
                        <Box className="tlc-progress-bar-container">
                            <Box className="tlc-progress-fill" style={{ width: '20%' }}></Box>
                        </Box>
                        <Box className="tlc-progress-labels">
                            <span>0</span>
                            <span>100%</span>
                        </Box>
                    </Box>

                    {/* Written Communication */}
                    <Box className="tlc-parameter-card">
                        <Box className="tlc-parameter-header">
                            <Box className="tlc-parameter-icon">
                                <SubjectOutlinedIcon fontSize="small" />
                            </Box>
                            <Box className="tlc-parameter-info">
                                <Typography className="tlc-parameter-title">Written Communication</Typography>
                                <Typography className="tlc-parameter-desc">Grammar and structure</Typography>
                            </Box>
                            <Box className="tlc-parameter-score-box">
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                                    <span className="tlc-parameter-score-top">6</span>
                                    <span style={{ fontSize: '13px', color: '#1976d2' }}>/ 30%</span>
                                </Box>
                                <span className="tlc-parameter-score-bottom">Weighted average</span>
                            </Box>
                        </Box>
                        <Box className="tlc-progress-bar-container">
                            <Box className="tlc-progress-fill" style={{ width: '20%' }}></Box>
                        </Box>
                        <Box className="tlc-progress-labels">
                            <span>0</span>
                            <span>100%</span>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box className="tlc-feedback-section">
                <Typography sx={{ fontWeight: 700, color: '#212529', marginBottom: '10px', fontSize: '14px' }}>
                    Mentor's Qualitative Feedback
                </Typography>
                <TextareaAutosize
                    className="tlc-feedback-textarea"
                    placeholder="Add specific observations about the learner's progress..."
                    minRows={1}
                    readOnly={isAdmin}
                />
            </Box>

            <Box className="tlc-sticky-footer">
                {/* <Box className="tlc-footer-status">
                    <HistoryOutlinedIcon fontSize="small" />
                    <span>Last saved: 2 mins ago</span>
                </Box> */}
                {!isAdmin && (
                    <Box className="tlc-footer-actions">
                        <Button variant="outlined" color="primary" sx={{ textTransform: 'none', fontWeight: 600, padding: '6px 24px', borderRadius: '4px' }}>
                            Save
                        </Button>
                        <Button variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 600, padding: '6px 24px', borderRadius: '4px', boxShadow: 'none' }}>
                            Mark as Reviewed &amp; Next
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default TlcPage;
